import {
  resolveContractingGuide,
  type CarrierRouteMap,
  type ContractingGuideLink,
  type ContractingGuideSectionId,
  type ResolvedContractingGuideSection,
} from "@aam/shared";
import { Badge, Card, CardDescription, CardTitle } from "../ui/index.js";

export type GuideContract = {
  carrier: string;
  imo: string;
  status: string;
};

const ctaClass =
  "focus-ring inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-accent-strong bg-accent-strong px-4 py-2.5 text-sm font-medium text-on-accent hover:brightness-95";

function GuideLinkButton({ link }: { link: ContractingGuideLink }) {
  return (
    <a href={link.href} target="_blank" rel="noreferrer" className={ctaClass}>
      {link.label} <span aria-hidden>↗</span>
    </a>
  );
}

/**
 * Next steps for carriers that leave Altura by Reagan.AI, a direct carrier
 * invite, or our contracting team. Insurance Bay carriers are handled by the
 * Insurance Bay button above, so they have no card here.
 */
export function ContractingOnboardingGuide({
  contracts,
  routes,
  hiddenSectionIds = [],
}: {
  contracts: GuideContract[];
  routes?: CarrierRouteMap;
  /** Sections held back until an earlier path is finished. */
  hiddenSectionIds?: readonly ContractingGuideSectionId[];
}) {
  const sections = resolveContractingGuide(contracts, routes).filter(
    (section) => !hiddenSectionIds.includes(section.id)
  );
  if (sections.length === 0) return null;

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-display text-xl font-bold text-content-primary">
          Finish these contracting steps
        </h3>
        <p className="mt-1 text-sm text-content-secondary">
          Some carriers still need you to watch for a specific email. Only the
          steps for your pending or active appointments are shown.
        </p>
      </div>
      {sections.map((section) => (
        <GuideSectionCard key={section.id} section={section} />
      ))}
    </div>
  );
}

function GuideSectionCard({ section }: { section: ResolvedContractingGuideSection }) {
  const link = section.link;
  return (
    <Card padding="lg" className="border-2 border-status-pending-border bg-status-pending-bg">
      <CardTitle>{section.title}</CardTitle>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {section.matchingLabels.map((label) => (
          <Badge key={label} variant="outline">
            {label}
          </Badge>
        ))}
      </div>
      <CardDescription className="mt-3">
        {section.summary}
      </CardDescription>
      <ol className="mt-4 list-decimal space-y-2 pl-5 text-sm leading-5 text-content-primary">
        {section.steps.map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>
      {section.notes?.length ? (
        <ul className="mt-3 space-y-1 text-xs leading-5 text-content-secondary">
          {section.notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      ) : null}
      {link && (
        <div className="mt-5">
          <GuideLinkButton link={link} />
        </div>
      )}
      {section.contact && (
        <p className="mt-4 text-xs text-content-secondary">
          Questions:{" "}
          <a
            href={`mailto:${section.contact}`}
            className="font-medium text-accent underline-offset-2 hover:underline"
          >
            {section.contact}
          </a>
        </p>
      )}
    </Card>
  );
}
